import moment from 'moment/moment'

import agent from 'agent'
import {
  type AttackFormOnSubmit,
  ProvenanceTargetType,
  TargetType
} from 'components/attacks/create/create-attack-types'
import { generateAttackConfiguration } from 'components/attacks/create/create-attack-utils'
import {
  getSelectedKubernetesTargets,
  getSelectedNamespace,
  getTotalSelectedK8Pods,
  type TargetsByOwner
} from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { getActualTargetsImpacted } from 'components/attacks/create/targets/target-utils'
import {
  type InfraImpactFormValues,
  type InfraTargetFormValues,
  type InfraTriggerFormValues
} from 'components/attacks/create/utils/infra-initial-form-state'
import toast from 'lib/toast'
import { type SnakeCaseTrafficImpactMapping } from 'types/api/attacks/details/create'
import { type InfraAttack } from 'types/api/attacks/details/infra-attack'
import {
  type AttackConfiguration,
  type ContainerSelection,
  InfraStrategyType,
  type InfraTarget,
  type KubernetesTargetingStrategy,
  type TargetEntry
} from 'types/api/attacks/details/targeting'
import { type K8sSubAttack } from 'types/api/kubernetes/attacks'
import { type K8sClient, type K8sObject } from 'types/api/kubernetes/kubernetes'
import { type AttackTrigger } from 'types/api/schedules/active'
import { type UserDefinedService } from 'types/api/services/service'
import { type Metadata } from 'types/metadata'
import { arrayToObject, keysToCamelCase } from 'utils/obj'
import { messageFromResponseError } from 'utils/strings'

/**
 * Constants
 */
export const ACTIVE_STAGES = [
  'selectTarget',
  'target',
  'impact',
  'trigger'
] as const

export const FORCE_NAMESPACE_SELECTION_LIMIT = 500

/**
 * Types and interfaces
 */
export enum K8sTargetingSelectionType {
  Exact = 'Exact',
  Selectors = 'Selectors'
}

// This type represents the initial state for the KubernetesAttackForm. Most explicitly used when rerunning attacks
export type KubernetesTargetFormValues = InfraTarget & {
  exact?: number
  impactSelectionType?: 'exact' | 'percentage'
  includeNewTargets?: boolean
  continueOnEmptyTargets?: boolean
  labelSelector?: string
  percentage?: number
  selectedClusterId: string // Actual name of the K8s cluster
  selectedClientId: string // uid associated with the K8s cluster
  selectedNamespace?: string
  selectedObjects: Set<string>
  targetAll: boolean // always false
  targetParams: Record<string, never>
  targetingSelectionType?: K8sTargetingSelectionType
  targetingStrategyType?: InfraStrategyType
}

export interface ScheduledK8sAttack extends K8sSubAttack {
  trigger?: AttackTrigger
}

export interface ImpactInitialState {
  args: string[]
  providers: string[]
  infra_command: any
  containerSelection?: ContainerSelection
  traffic_impact_mapping: SnakeCaseTrafficImpactMapping
  continueOnEmptyTargets?: boolean
}

/**
 * Form validation
 */
export function validateK8Targets(
  values: {
    exact?
    percentage?
    selectedObjects?: Set<string> // ObjectIds
    selectedClusterId?: string
    targetingSelectionType?: K8sTargetingSelectionType
  },
  targets: K8sObject[] = [],
  targetsByOwners: TargetsByOwner[] = []
): Record<string, unknown> {
  const targetsLength = targets.length
  const errors: Record<string, string> = {}
  const { exact, percentage, selectedObjects, targetingSelectionType } = values

  // Note: the selection in k8s attacks is for PODS, not for targets/clients
  const maxPods = getTotalSelectedK8Pods(selectedObjects, targetsByOwners)

  const totalSelectedPodsForPercentage = getActualTargetsImpacted(
    maxPods,
    percentage
  )

  const isExactMode = exact !== undefined
  const isPercentageMode = percentage !== undefined

  if (!values.selectedClusterId) {
    errors.clusterId = 'No cluster Id selected'
  } else if (targetsLength === 0) {
    errors.targets = 'No targets installed'
  } else {
    if (
      maxPods === 0 &&
      targetingSelectionType !== K8sTargetingSelectionType.Selectors
    ) {
      errors.targeting = 'You must have at least one valid object selected'
    }

    if (
      selectedObjects?.size === 0 &&
      targetingSelectionType === K8sTargetingSelectionType.Selectors
    ) {
      errors.targeting = 'You must have at least one valid object selected'
    }

    if (isExactMode) {
      if (exact > maxPods) {
        errors.exact =
          'The number of targeted pods cannot exceed the number of available targets'
      } else if (exact < 1) {
        errors.exact = 'You must target at least one pod'
      }
    }

    if (isPercentageMode) {
      if (percentage < 1) {
        errors.percentage = 'The percentage of targets must be at least 1'
      } else if (percentage > 100) {
        errors.percentage = 'The percentage of targets cannot be more than 100'
      } else if (totalSelectedPodsForPercentage === 0) {
        errors.percentage = 'Actual impacted targets must be at least 1'
      }
    }
  }

  return errors
}

/**
 * API submit functions
 */
export const createK8Attack: AttackFormOnSubmit = async function createK8Attack(
  _targetType,
  { target, impact, trigger },
  metadata,
  currentTeamId,
  navigate,
  meta
) {
  const body: ScheduledK8sAttack =
    generateKubernetesAttackConfigurationRequestBody(
      { target, impact, trigger },
      metadata,
      meta.k8sTargets
    )

  try {
    const { data } = await agent.post(`/attacks?teamId=${currentTeamId}`, body)

    // Redirect to new attack page if trigger is enabled, otherwise redirect to schedules page
    navigate(
      body.trigger ? '/schedules' : `/attacks/kubernetes/${data[0].k8sAttackId}`
    )
  } catch (err) {
    toast({
      type: 'error',
      text: messageFromResponseError(
        err,
        'There was an issue unleashing this experiment. Please try again later.'
      )
    })
    // throw so that we can avoid resetting the form on failure (caught in onSubmit in InfraAttackForm)
    throw err
  }
}

/**
 * Utility functions
 */
export function buildLabelSelectionObject(
  labelSelector: string,
  clusterId: string,
  namespace: string
) {
  const baseRequestPayload =
    namespace === 'All' ? { allNamespaces: true } : { namespaces: [namespace] }
  return {
    labelSelector,
    clusterId,
    ...baseRequestPayload
  }
}

/**
 * Return true if the parentTarget or any of its children are selected
 */
export function isTargetSelected(
  parentTarget: TargetsByOwner,
  selectedObjects: Set<string>
): boolean {
  const { target, children } = parentTarget
  if (selectedObjects.has(target.uid)) {
    return true
  }

  return Object.values(children)
    .flat()
    .some((child) => selectedObjects.has(child.uid))
}

export function generateKubernetesAttackConfigurationRequestBody(
  {
    target,
    impact,
    trigger
  }: {
    target: InfraTargetFormValues | KubernetesTargetFormValues
    impact: InfraImpactFormValues
    trigger: InfraTriggerFormValues
  },
  metadata: Metadata,
  targets: K8sObject[]
): ScheduledK8sAttack {
  const attackConfiguration = generateAttackConfiguration(
    TargetType.Kubernetes,
    { target, impact },
    metadata,
    targets
  )

  // Kubernetes API takes an Attack which is also schedulable so we have to add trigger if present
  const body: ScheduledK8sAttack = {
    attackConfiguration,
    ...('includeNewTargets' in target && {
      includeNewTargets: Boolean(target.includeNewTargets)
    }),
    ...('continueOnEmptyTargets' in impact && {
      continueOnEmptyTargets: Boolean(impact.continueOnEmptyTargets)
    })
  }

  if (trigger?.enabled) {
    if (trigger.type === 'Once') {
      const date = moment(trigger.exactDate).format('MM/DD/YYYY')
      body.trigger = {
        type: trigger.type,
        exact: moment(`${date} ${trigger.exactTime}`).toISOString()
      }
    } else {
      body.trigger = {
        type: trigger.type,
        start: trigger.start,
        end: trigger.end,
        activeDays: trigger.activeDays,
        maxRuns: trigger.maxRuns,
        timeZone: trigger.timeZone
      }
    }
  }

  return body
}

function getSelectedClientId(
  selectedAssetId: string | undefined,
  k8sObjects: K8sObject[],
  objectIdsByClientIds: Record<string, Set<string>>,
  clients: K8sClient[]
) {
  if (selectedAssetId) {
    return selectedAssetId
  }

  const firstObjectUid = k8sObjects[0]?.uid

  const foundClient =
    firstObjectUid && k8sObjects.length
      ? clients.find((client) => {
          const objectByClientId = objectIdsByClientIds[client.clientId]
          return objectByClientId?.has(firstObjectUid)
        })
      : undefined

  // Returns undefined if no client id is found
  return foundClient?.clientId
}

function getFilteredK8sObjectIds(
  selectedClientId: string | undefined,
  clientIdClusterIdMap: Record<string, string>,
  k8sObjects: K8sObject[],
  k8sTargetEntry: TargetEntry | UserDefinedService | undefined,
  isServiceAttack: boolean
) {
  return selectedClientId
    ? k8sObjects
        .filter(({ clusterId, name, uid }) => {
          const hasSelectedClusterId =
            clusterId === clientIdClusterIdMap[selectedClientId]

          if (!isServiceAttack) {
            return hasSelectedClusterId
          }

          if (k8sTargetEntry?.targetingStrategy?.ids) {
            return (
              hasSelectedClusterId &&
              k8sTargetEntry.targetingStrategy.ids.includes(uid)
            )
          }

          return (
            hasSelectedClusterId &&
            k8sTargetEntry?.targetingStrategy?.names
              .map(({ name }) => name)
              .includes(name)
          )
        })
        .map((t) => t.uid)
    : k8sObjects.map((t) => t.uid)
}

const CLUSTER_ID_FALLBACK = ''

export function createTargetingStrategyFromKubernetesForm(
  target: KubernetesTargetFormValues,
  k8sTargets: K8sObject[],
  impact: InfraImpactFormValues
): KubernetesTargetingStrategy {
  const targetingStrategy: KubernetesTargetingStrategy = {
    type: ProvenanceTargetType.Kubernetes
  }

  if (target.targetAll) {
    targetingStrategy.isAll = Boolean(target.targetAll)
  } else {
    targetingStrategy.ids = getSelectedKubernetesTargets(
      k8sTargets,
      target
    ).map(({ uid }) => uid)
  }

  if (impact.containerSelection.selectionType) {
    targetingStrategy.containerSelection = {
      selectionType: impact.containerSelection.selectionType.toUpperCase(),
      containerNames: [...impact.containerSelection.containerNames]
    }
  }

  return targetingStrategy
}

export function formatKubernetesTargetParams(
  k8Attack: {
    attack?: K8sSubAttack
    attackConfiguration?: AttackConfiguration
    targetAll?: boolean
    uid?: string
    includeNewTargets?: boolean
  } = {},
  clients: K8sClient[] = [],
  forceNamespaceSelectionLimit: number,
  selectedAssetId?: string,
  service?: UserDefinedService
): KubernetesTargetFormValues {
  const objectIdsByClientIds = arrayToObject({
    data: clients,
    keyFn: (item) => item.clientId,
    valueFn: (item) => new Set(item.objects.map((k8Object) => k8Object.uid))
  })

  /*
    In k8s clients:
    clusterId = display name of cluster
    clientId = actual id of the client
    confused...so am I and they are used interchangeably with incongruent variable names
    just be aware of what you actually are trying to access.
   */
  const clientIdClusterIdMap = arrayToObject({
    data: clients,
    keyFn: (item) => item.clientId,
    valueFn: (item) => item.clusterId
  })

  const k8sAttackCamelCase = keysToCamelCase(k8Attack)

  const { attack = null, attackConfiguration } = k8sAttackCamelCase

  const isServiceAttack = Boolean(service)

  // We need to identify the targetingStrategy parent. When creating/editing a service attack, this will be the service
  // itself.  When creating/editing any other attack, this will be pulled off of the attackConfiguration.
  const k8sTargetEntry: TargetEntry | UserDefinedService | undefined =
    service ??
    attackConfiguration?.targets?.find(
      (target) => target.targetingStrategy?.type === 'Kubernetes'
    )

  // Checks for both existence and null value of the labelSelection attribute
  const isLabelSelectorPresent = Boolean(
    k8sTargetEntry?.targetingStrategy?.labelSelection
  )

  const initialTargetParams: KubernetesTargetFormValues = {
    selectedClientId: '',
    selectedClusterId: '',
    selectedNamespace: '',
    selectedObjects: new Set(),
    targetAll: false,
    targetParams: {},
    targetingStrategyType: InfraStrategyType.Random,
    targetingSelectionType: K8sTargetingSelectionType.Selectors
  }

  if (isLabelSelectorPresent) {
    const {
      labelSelector,
      allNamespaces,
      clusterId: selectedClusterId,
      namespaces
    } = k8sTargetEntry?.targetingStrategy?.labelSelection ?? {}

    const selectedClientId =
      Object.keys(clientIdClusterIdMap).find(
        (clientId) => clientIdClusterIdMap[clientId] === selectedClusterId
      ) || CLUSTER_ID_FALLBACK

    const selectedNamespace = allNamespaces ? 'All' : namespaces[0]
    initialTargetParams.labelSelector = labelSelector
    initialTargetParams.percentage = 100
    initialTargetParams.selectedClientId = selectedClientId
    initialTargetParams.selectedClusterId = selectedClusterId
    initialTargetParams.selectedNamespace = selectedNamespace
    // this flag can exist as a field in either object reference. Both should be checked here.
    // Otherwise, the Scenario form checkbox state will not initialize with the saved value.
    initialTargetParams.includeNewTargets =
      k8Attack.includeNewTargets ?? attack?.includeNewTargets

    if (attackConfiguration?.sampling?.type === 'Even') {
      initialTargetParams.percentage = attackConfiguration.sampling.percent
    } else {
      initialTargetParams.exact = attackConfiguration?.sampling.count
    }
  } else {
    const k8sObjects = clients
      .flatMap((client) => client.objects)
      .filter((object) => {
        const { ids, names = [] } =
          (k8sTargetEntry?.targetingStrategy as KubernetesTargetingStrategy) ||
          {}

        // Return everything for service attacks
        if (isServiceAttack) {
          return true
        }

        if (ids) {
          return k8sTargetEntry?.targetingStrategy?.ids?.includes(object.uid)
        }

        // Names are relatively ambiguous, so we have to match name, namespace, (also clusterId if present) and kind
        return (
          names?.filter(
            (target) =>
              target.name === object.name &&
              target.namespace === object.namespace &&
              (target.clusterId === object.clusterId || !target.clusterId) &&
              target.kind === object.kind
          ).length > 0
        )
      })

    const percentage = attack?.targetDefinition?.strategy?.percentage
    const exact = attack?.targetDefinition?.strategy?.count

    const selectedClientId =
      getSelectedClientId(
        selectedAssetId,
        k8sObjects,
        objectIdsByClientIds,
        clients
      ) || CLUSTER_ID_FALLBACK

    const selectedClusterId = clientIdClusterIdMap[selectedClientId]

    const selectedNamespace = selectedClientId
      ? getSelectedNamespace(
          selectedClientId,
          clients,
          forceNamespaceSelectionLimit,
          k8sObjects
        )
      : k8sObjects[0]?.namespace

    initialTargetParams.targetAll = Boolean(k8sAttackCamelCase.targetAll)
    initialTargetParams.selectedClientId = selectedClientId
    initialTargetParams.selectedClusterId = selectedClusterId
    initialTargetParams.selectedNamespace = selectedNamespace

    // Indicates editing an existing attack
    if (attackConfiguration || service) {
      initialTargetParams.targetingSelectionType =
        K8sTargetingSelectionType.Exact
    }

    if (percentage != null) {
      initialTargetParams.percentage = percentage
    } else if (exact != null) {
      initialTargetParams.exact = exact
    } else {
      initialTargetParams.percentage = 100
    }

    if (!k8sAttackCamelCase.uid) {
      initialTargetParams.selectedObjects = new Set()
    } else {
      const filteredK8sObjectIds = getFilteredK8sObjectIds(
        selectedClientId,
        clientIdClusterIdMap,
        k8sObjects,
        k8sTargetEntry,
        isServiceAttack
      )

      // For Scenarios the "includeNewTargets" value is on the Node, for Ad-hoc Attacks it's on the SubAttack
      const includeNewTargets =
        k8Attack.includeNewTargets ?? attack?.includeNewTargets

      initialTargetParams.includeNewTargets = Boolean(includeNewTargets)
      initialTargetParams.selectedObjects = new Set(filteredK8sObjectIds)
    }
  }

  return {
    ...initialTargetParams,
    // We want to set this as default to true: EN-7022
    includeNewTargets: initialTargetParams.includeNewTargets ?? true
  }
}

function getInfraCommandArgsForAttack(attack) {
  if (attack.attackConfiguration || attack.attack_configuration) {
    const attackConfig: AttackConfiguration =
      keysToCamelCase(attack).attackConfiguration

    return attackConfig.command?.infraCommandArgs
  }

  return attack.attack?.impactDefinition
}

function getTargetDefinitionForAttack(attack) {
  if (attack.attackConfiguration || attack.attack_configuration) {
    const attackConfig: AttackConfiguration =
      keysToCamelCase(attack).attackConfiguration

    return attackConfig.targets[0]
  }

  return attack.attack?.targetDefinition
}

export function formatImpactParams(
  k8Attack: Partial<InfraAttack> = {}
): ImpactInitialState {
  const { attack = {} } = k8Attack
  const infraCommandArgs = getInfraCommandArgsForAttack(k8Attack)
  const cliArgs = infraCommandArgs?.cliArgs ?? []

  const { multiSelectTags: multi_select_tags, ...rest } =
    infraCommandArgs?.trafficImpactMapping ?? {}

  const finalParams: ImpactInitialState = {
    args: cliArgs,
    providers: infraCommandArgs?.providers ?? [],
    traffic_impact_mapping: {
      ...rest,
      // multi_select_tags needs to be in snake case
      multi_select_tags
    },
    infra_command: {
      commandType: cliArgs[0]
    },
    continueOnEmptyTargets:
      k8Attack.continue_on_empty_targets ??
      k8Attack.continueOnEmptyTargets ??
      attack.continueOnEmptyTargets ??
      false
  }

  const targetDefinition = getTargetDefinitionForAttack(k8Attack)

  // TODO: targetingDefinition is deprecated, use attackConfiguration going forward
  finalParams.containerSelection = attack?.targetDefinition?.strategy
    ?.containerSelection ??
    targetDefinition?.containerSelection ??
    // for existing scenarios
    k8Attack?.attackConfiguration?.targets[0]?.targetingStrategy
      ?.containerSelection ?? {
      containerNames: [],
      selectionType: 'ANY'
    }

  return finalParams
}
